import type { ActiveContext, DocumentContextChunk } from '../types';
import { isAmbiguousDocumentCountFollowUp } from './documentFollowUp';

interface SpreadsheetStructuredAnswer {
  answer: string;
  groundingSource: string;
  citations: Array<{ id: number; title: string; url: string; snippet?: string }>;
}

interface TableRow {
  values: Record<string, string>;
}

interface StructuredTable {
  name: string;
  headers: string[];
  rows: TableRow[];
}

interface SpreadsheetDataset {
  fileName: string;
  tables: StructuredTable[];
}

interface PlannedColumn {
  label: string;
  source?: string;
  kind?: 'age_bucket' | 'installed_year' | 'refrigerant_group' | 'building_or_property';
}

type SortDirection = 'asc' | 'desc';

interface TablePlan {
  operation: 'coverage' | 'grouped_rows' | 'pivot_count' | 'group_count' | 'sorted_rows';
  table: StructuredTable;
  rows: TableRow[];
  label: string;
  groupColumns: PlannedColumn[];
  dateColumn?: string;
  sortDirection?: SortDirection;
}

const STRUCTURED_GROUNDING_SOURCE = 'document_analysis';
const MAX_DISTINCT_VALUES_FOR_AUTO_FILTER = 40;

const COLUMN_ALIASES = {
  property: ['property', 'asset', 'site', 'location'],
  building: ['building', 'bldg'],
  unit: ['unit', 'suite', 'space'],
  equipment: ['equipment', 'asset name', 'description'],
  type: ['type', 'equipment type', 'category'],
  manufacturer: ['manufacturer', 'make'],
  model: ['model', 'model number'],
  serial: ['serial', 'serial number'],
  installedDate: ['installed date', 'install date', 'installation date', 'date installed', 'placed in service'],
  date: ['date', 'due date', 'expiration date', 'start date', 'end date', 'installed date', 'install date'],
  refrigerant: ['refrigerant', 'refrigerant type', 'refrig', 'refridgerant'],
  tonnage: ['tonnage', 'tons'],
  status: ['status', 'state'],
  owner: ['owner', 'assignee', 'assigned to', 'manager'],
} as const;

const DOMAIN_VALUE_ALIASES: Array<{ columnConcept: keyof typeof COLUMN_ALIASES; value: string; patterns: RegExp[] }> = [
  {
    columnConcept: 'type',
    value: 'RTU',
    patterns: [/\brtus?\b/i, /\brooftop units?\b/i],
  },
];

function normalizeWhitespace(value: string): string {
  return value.replace(/\r/g, '').replace(/\s+/g, ' ').trim();
}

function normalizeToken(value: string): string {
  return normalizeWhitespace(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function splitTableSections(content: string): Array<{ name: string; text: string }> {
  const normalized = content.replace(/\r/g, '');
  const markerPattern = /^##\s+(Sheet|Table):\s+(.+)$/gim;
  const matches = Array.from(normalized.matchAll(markerPattern));

  if (matches.length === 0) {
    return [{ name: 'Uploaded table', text: normalized.trim() }];
  }

  return matches.map((match, index) => {
    const start = (match.index || 0) + match[0].length;
    const end = index + 1 < matches.length ? matches[index + 1].index || normalized.length : normalized.length;
    return {
      name: normalizeWhitespace(match[2] || `${match[1]} ${index + 1}`),
      text: normalized.slice(start, end).trim(),
    };
  });
}

function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];

    if (char === '"') {
      if (inQuotes && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      cells.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  cells.push(current);
  return cells.map((cell) => cell.trim());
}

function parseDelimitedRows(text: string): string[][] {
  const lines = text
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => line.trim().length > 0);

  if (lines.some((line) => line.includes(','))) {
    return lines.map((line) => parseCsvLine(line));
  }

  if (lines.some((line) => line.includes('\t'))) {
    return lines.map((line) => line.split('\t').map((cell) => cell.trim()));
  }

  return [];
}

function looksLikeNumber(value: string): boolean {
  return /^[$(]?\d[\d,.\s%)]*$/.test(value.trim());
}

function looksLikeDate(value: string): boolean {
  return /\b(19|20)\d{2}\b/.test(value) || /\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/.test(value);
}

function scoreHeaderRow(rows: string[][], index: number): number {
  const cells = rows[index].map(normalizeWhitespace);
  const nonEmpty = cells.filter(Boolean);
  if (nonEmpty.length < 2) return Number.NEGATIVE_INFINITY;

  const next = rows[index + 1] || [];
  const nextNonEmpty = next.filter((cell) => normalizeWhitespace(cell).length > 0).length;
  const unique = new Set(nonEmpty.map((cell) => normalizeToken(cell))).size;
  const numericLike = nonEmpty.filter((cell) => looksLikeNumber(cell) || looksLikeDate(cell)).length;
  const aliasHits = nonEmpty.filter((cell) =>
    Object.values(COLUMN_ALIASES).flat().some((alias) => normalizeToken(cell) === normalizeToken(alias))
  ).length;

  return nonEmpty.length * 3 + unique + aliasHits * 5 + Math.min(nextNonEmpty, nonEmpty.length) - numericLike * 3;
}

function findHeaderIndex(rows: string[][]): number {
  let bestIndex = -1;
  let bestScore = Number.NEGATIVE_INFINITY;

  rows.forEach((_, index) => {
    const score = scoreHeaderRow(rows, index);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });

  return bestScore > 0 ? bestIndex : -1;
}

function dedupeHeaders(headers: string[]): string[] {
  const seen = new Map<string, number>();
  return headers.map((rawHeader, index) => {
    const base = normalizeWhitespace(rawHeader).replace(/^\uFEFF/, '') || `Column ${index + 1}`;
    const normalized = normalizeToken(base);
    const count = seen.get(normalized) || 0;
    seen.set(normalized, count + 1);
    return count === 0 ? base : `${base} ${count + 1}`;
  });
}

function parseStructuredTable(section: { name: string; text: string }): StructuredTable | null {
  const rows = parseDelimitedRows(section.text).filter((row) => row.some((cell) => normalizeWhitespace(cell)));
  if (rows.length < 2) return null;

  const headerIndex = findHeaderIndex(rows);
  if (headerIndex === -1) return null;

  const headers = dedupeHeaders(rows[headerIndex]);
  const tableRows: TableRow[] = rows.slice(headerIndex + 1)
    .map((row) => {
      const values: Record<string, string> = {};
      headers.forEach((header, index) => {
        values[header] = normalizeWhitespace(row[index] || '');
      });
      return { values };
    })
    .filter((row) => Object.values(row.values).some((value) => value.length > 0));

  return tableRows.length > 0 ? { name: section.name, headers, rows: tableRows } : null;
}

function parseStructuredDataset(content: string, fileName: string): SpreadsheetDataset | null {
  const tables = splitTableSections(content)
    .map((section) => parseStructuredTable(section))
    .filter((table): table is StructuredTable => table !== null);

  return tables.length > 0 ? { fileName, tables } : null;
}

function getSpreadsheetDataset(documentContents: DocumentContextChunk[]): SpreadsheetDataset | null {
  const fullDocument = documentContents.find((doc) => doc.selectionMode === 'full_document' && typeof doc.content === 'string');
  if (!fullDocument?.content) return null;
  return parseStructuredDataset(fullDocument.content, fullDocument.fileName);
}

function matchColumn(headers: string[], concept: keyof typeof COLUMN_ALIASES): string | undefined {
  const aliases = COLUMN_ALIASES[concept].map(normalizeToken);
  return headers.find((header) => {
    const normalized = normalizeToken(header);
    return aliases.some((alias) => normalized === alias || normalized.includes(alias));
  });
}

function matchColumnFromQuery(headers: string[], query: string): string[] {
  const normalizedQuery = normalizeToken(query);
  return headers
    .map((header) => {
    const normalizedHeader = normalizeToken(header);
      if (normalizedHeader.length < 3) return { header, index: -1 };
      const pattern = new RegExp(`(^| )${normalizedHeader.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}( |$)`);
      const match = normalizedQuery.match(pattern);
      return { header, index: match?.index ?? -1 };
    })
    .filter((match) => match.index >= 0)
    .sort((left, right) => left.index - right.index)
    .map((match) => match.header);
}

function parseYear(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const match = value.match(/\b(19|20)\d{2}\b/);
  if (!match) return undefined;
  const year = Number.parseInt(match[0], 10);
  return Number.isFinite(year) ? year : undefined;
}

function parseDateTime(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const normalized = normalizeWhitespace(value);
  if (!normalized) return undefined;

  const parsed = Date.parse(normalized);
  if (Number.isFinite(parsed)) return parsed;

  const year = parseYear(normalized);
  if (typeof year === 'number') return Date.UTC(year, 0, 1);

  return undefined;
}

function getDateColumn(table: StructuredTable, query: string): string | undefined {
  const installedDate = matchColumn(table.headers, 'installedDate');
  if (installedDate && /\b(age|install|installed|installation)\b/i.test(query)) return installedDate;

  const explicitDate = matchColumn(table.headers, 'date');
  if (explicitDate) return explicitDate;

  return table.headers.find((header) =>
    table.rows.slice(0, 10).some((row) => looksLikeDate(row.values[header] || ''))
  );
}

function getAgeBucket(year: number | undefined): string {
  if (typeof year !== 'number') return 'No Install Date';
  if (year >= 2020) return '2020-2026';
  if (year >= 2010) return '2010-2019';
  if (year >= 2000) return '2000-2009';
  if (year >= 1990) return '1990-1999';
  return 'Pre-1990';
}

function normalizeRefrigerant(value: string | undefined): string | undefined {
  const text = normalizeWhitespace(value || '');
  if (!text) return undefined;

  const compact = text.replace(/[\s_-]/g, '').toUpperCase();
  if (compact.includes('R22') || compact === '22') return 'R22';
  if (compact.includes('410A')) return 'R410A';
  if (compact.includes('454B')) return 'R454B';
  if (compact.includes('R32') || compact === '32') return 'R32';
  return text;
}

function inferRefrigerant(row: TableRow, table: StructuredTable, dateColumn?: string): { group: string; basis: string } {
  const refrigerantColumn = matchColumn(table.headers, 'refrigerant');
  const listed = normalizeRefrigerant(refrigerantColumn ? row.values[refrigerantColumn] : undefined);
  if (listed) {
    return { group: listed, basis: `Listed refrigerant: ${row.values[refrigerantColumn || '']}` };
  }

  const modelColumn = matchColumn(table.headers, 'model');
  const manufacturerColumn = matchColumn(table.headers, 'manufacturer');
  const model = normalizeWhitespace(modelColumn ? row.values[modelColumn] : '').toUpperCase();
  const manufacturer = normalizeWhitespace(manufacturerColumn ? row.values[manufacturerColumn] : '').toUpperCase();
  const year = parseYear(dateColumn ? row.values[dateColumn] : undefined);

  if (typeof year !== 'number') {
    return { group: 'Unknown', basis: 'No listed refrigerant and no install year available' };
  }

  if (year <= 2009) return { group: 'Likely R22', basis: 'Inferred from pre-2010 install year with blank refrigerant' };
  if (year <= 2024) return { group: 'Likely R410A', basis: 'Inferred from 2010-2024 install year with blank refrigerant' };
  if (manufacturer.includes('DAIKIN') || model.includes('DSG')) return { group: 'Likely R32', basis: 'Inferred from 2025+ Daikin/DSG model with blank refrigerant' };
  if (manufacturer.includes('LENNOX') || manufacturer.includes('YORK') || model.includes('LGX') || model.includes('KE')) return { group: 'Likely R454B', basis: 'Inferred from 2025+ Lennox/York low-GWP platform with blank refrigerant' };
  return { group: 'Likely R32/R454B', basis: 'Inferred from 2025+ install year with blank refrigerant' };
}

function columnValue(row: TableRow, table: StructuredTable, column: PlannedColumn, dateColumn?: string): string {
  if (column.kind === 'age_bucket') return getAgeBucket(parseYear(dateColumn ? row.values[dateColumn] : undefined));
  if (column.kind === 'installed_year') return String(parseYear(dateColumn ? row.values[dateColumn] : undefined) || '');
  if (column.kind === 'refrigerant_group') return inferRefrigerant(row, table, dateColumn).group;
  if (column.kind === 'building_or_property') {
    const buildingColumn = matchColumn(table.headers, 'building');
    const propertyColumn = matchColumn(table.headers, 'property');
    return normalizeWhitespace((buildingColumn ? row.values[buildingColumn] : '') || (propertyColumn ? row.values[propertyColumn] : ''));
  }
  return column.source ? normalizeWhitespace(row.values[column.source] || '') : '';
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function csvCell(value: string | number | undefined): string {
  const text = String(value ?? '');
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function csvRow(values: Array<string | number | undefined>): string {
  return values.map(csvCell).join(',');
}

function choosePrimaryTable(dataset: SpreadsheetDataset): StructuredTable {
  return [...dataset.tables].sort((left, right) => right.rows.length - left.rows.length)[0];
}

function distinctValues(table: StructuredTable, header: string): string[] {
  return Array.from(new Set(table.rows.map((row) => normalizeWhitespace(row.values[header] || '')).filter(Boolean)));
}

function filterRows(table: StructuredTable, query: string): { rows: TableRow[]; label: string } {
  const normalizedQuery = normalizeToken(query);
  let rows = table.rows;
  const labels: string[] = [];
  const expectedCounts = Array.from(normalizedQuery.matchAll(/\b\d+\b/g))
    .map((match) => Number.parseInt(match[0], 10))
    .filter((value) => Number.isFinite(value));

  for (const alias of DOMAIN_VALUE_ALIASES) {
    if (!alias.patterns.some((pattern) => pattern.test(query))) continue;
    const column = matchColumn(table.headers, alias.columnConcept);
    if (!column || !distinctValues(table, column).some((value) => normalizeToken(value) === normalizeToken(alias.value))) continue;
    rows = rows.filter((row) => normalizeToken(row.values[column] || '') === normalizeToken(alias.value));
    labels.push(alias.value);
  }

  if (labels.length === 0 && expectedCounts.length > 0) {
    for (const header of table.headers) {
      const values = distinctValues(table, header)
        .filter((value) => value.length >= 2 && value.length <= 40 && !/^\d+$/.test(value));
      if (values.length === 0 || values.length > MAX_DISTINCT_VALUES_FOR_AUTO_FILTER) continue;

      const counts = new Map<string, number>();
      table.rows.forEach((row) => {
        const value = normalizeWhitespace(row.values[header] || '');
        if (!value || !values.includes(value)) return;
        counts.set(value, (counts.get(value) || 0) + 1);
      });

      const matched = Array.from(counts.entries()).find(([, count]) => expectedCounts.includes(count));
      if (matched) {
        rows = rows.filter((row) => normalizeWhitespace(row.values[header] || '') === matched[0]);
        labels.push(matched[0]);
        break;
      }
    }
  }

  for (const header of table.headers) {
    const values = distinctValues(table, header)
      .filter((value) => value.length >= 2 && value.length <= 40 && !/^\d+$/.test(value));
    if (values.length === 0 || values.length > MAX_DISTINCT_VALUES_FOR_AUTO_FILTER) continue;

    const matchedValue = values.find((value) => {
      const normalizedValue = normalizeToken(value);
      return normalizedValue.length >= 2 && new RegExp(`(^| )${normalizedValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}( |$)`).test(normalizedQuery);
    });

    if (matchedValue) {
      rows = rows.filter((row) => normalizeToken(row.values[header] || '') === normalizeToken(matchedValue));
      labels.push(matchedValue);
    }
  }

  return { rows, label: labels.length > 0 ? labels.join(', ') : 'rows' };
}

function queryRequestsAllRows(query: string): boolean {
  return /\b(return|show|list|include|organize|organized|group|grouped|sort|export)\b/i.test(query) &&
    /\b(all|every|each|complete|full|\d+\s+(rows?|units?|items?|records?)|\b\d{2,}\b)\b/i.test(query);
}

function queryRequestsCounts(query: string): boolean {
  return /\b(count|counts|summary|summarize|breakdown|rollup|grid|pivot|by year)\b/i.test(query);
}

function queryRequestsDateSort(query: string): boolean {
  return /\b(sort|sorted|order|ordered|ascending|descending|oldest|newest|latest|earliest)\b/i.test(query) &&
    /\b(date|install|installed|installation|year|oldest|newest|latest|earliest)\b/i.test(query);
}

function resolveSortDirection(query: string): SortDirection {
  if (/\b(desc|descending|newest|latest|recent)\b/i.test(query)) return 'desc';
  return 'asc';
}

function buildGroupColumns(table: StructuredTable, query: string, dateColumn?: string): PlannedColumn[] {
  const columns: PlannedColumn[] = [];
  const addColumn = (column: PlannedColumn) => {
    if (!columns.some((existing) => existing.label === column.label)) columns.push(column);
  };

  if (/\brefrigerant|refridgerant|refrig\b/i.test(query)) {
    addColumn({ label: 'Refrigerant Group', kind: 'refrigerant_group' });
  }

  if (/\bbuilding|bldg|property|site|asset|location\b/i.test(query)) {
    const buildingColumn = matchColumn(table.headers, 'building');
    const propertyColumn = matchColumn(table.headers, 'property');
    if (buildingColumn || propertyColumn) addColumn({ label: buildingColumn ? 'Building' : 'Property', kind: 'building_or_property' });
  }

  if (/\bage|install|installed|installation|year\b/i.test(query) && dateColumn) {
    addColumn({ label: 'Age Bucket', kind: 'age_bucket' });
  }

  const typeColumn = matchColumn(table.headers, 'type');
  matchColumnFromQuery(table.headers, query).forEach((header) => {
    if (header === typeColumn && /\b(refri(?:gerant|dgerant)|refrig)\s+type\b/i.test(query)) return;
    addColumn({ label: header, source: header });
  });
  return columns;
}

function buildPlanningText(
  query: string,
  activeContext?: ActiveContext,
  conversationHistory?: Array<{ role: string; content: string }>
): string {
  const recentUserQueries = (conversationHistory || [])
    .filter((message) => message.role === 'user' && typeof message.content === 'string')
    .slice(-4)
    .map((message) => message.content.slice(0, 500));

  return [
    query,
    activeContext?.lastTask,
    activeContext?.selectedOptionHint,
    ...recentUserQueries,
  ]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' ');
}

function buildPlan(
  query: string,
  dataset: SpreadsheetDataset,
  activeContext?: ActiveContext,
  conversationHistory?: Array<{ role: string; content: string }>
): TablePlan | null {
  const table = choosePrimaryTable(dataset);
  const planningText = buildPlanningText(query, activeContext, conversationHistory);
  const dateColumn = getDateColumn(table, planningText);
  const filtered = filterRows(table, planningText);
  const rows = filtered.rows;
  if (rows.length === 0) return null;

  if (isAmbiguousDocumentCountFollowUp(query, activeContext)) {
    return { operation: 'coverage', table, rows, label: filtered.label, groupColumns: [], dateColumn };
  }

  const groupColumns = buildGroupColumns(table, query, dateColumn);
  const propertyColumn = matchColumn(table.headers, 'property');
  const shouldSortByDate = Boolean(dateColumn && queryRequestsDateSort(planningText));

  if (queryRequestsCounts(query) && /\bgrid|pivot|year\b/i.test(query) && propertyColumn && dateColumn) {
    return {
      operation: 'pivot_count',
      table,
      rows,
      label: filtered.label,
      groupColumns: [{ label: 'Property', source: propertyColumn }],
      dateColumn,
    };
  }

  if (queryRequestsAllRows(query) && shouldSortByDate) {
    return {
      operation: 'sorted_rows',
      table,
      rows,
      label: filtered.label,
      groupColumns: [],
      dateColumn,
      sortDirection: resolveSortDirection(query),
    };
  }

  if (queryRequestsAllRows(query) && groupColumns.length > 0) {
    return { operation: 'grouped_rows', table, rows, label: filtered.label, groupColumns, dateColumn };
  }

  if ((queryRequestsCounts(query) || /\bage\b/i.test(query)) && groupColumns.length > 0) {
    return { operation: 'group_count', table, rows, label: filtered.label, groupColumns, dateColumn };
  }

  return null;
}

function buildCoverageAnswer(plan: TablePlan): string {
  const datedCount = plan.dateColumn ? plan.rows.filter((row) => parseYear(row.values[plan.dateColumn || ''])).length : 0;
  return [
    `Coverage from the uploaded table:`,
    '',
    `- Total matching rows: ${formatNumber(plan.rows.length)}`,
    ...(plan.dateColumn
      ? [
          `- Rows with ${plan.dateColumn}: ${formatNumber(datedCount)}`,
          `- Rows missing ${plan.dateColumn}: ${formatNumber(plan.rows.length - datedCount)}`,
        ]
      : []),
  ].join('\n');
}

function buildGroupKey(row: TableRow, plan: TablePlan): string {
  return plan.groupColumns.map((column) => columnValue(row, plan.table, column, plan.dateColumn) || '(blank)').join(' | ');
}

function buildGroupCountAnswer(plan: TablePlan): string {
  const counts = new Map<string, number>();
  plan.rows.forEach((row) => {
    const key = buildGroupKey(row, plan);
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  const lines = [
    `Here is the exact count from the uploaded table.`,
    '',
    `- Total matching rows: ${formatNumber(plan.rows.length)}`,
    `- Grouped by: ${plan.groupColumns.map((column) => column.label).join(', ')}`,
    '',
    `| ${plan.groupColumns.map((column) => column.label).join(' / ')} | Count |`,
    '| --- | ---: |',
  ];

  Array.from(counts.entries())
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .forEach(([key, count]) => lines.push(`| ${key} | ${formatNumber(count)} |`));

  return lines.join('\n');
}

function sortRowsForPlan(plan: TablePlan): TableRow[] {
  return [...plan.rows].sort((left, right) => {
    for (const column of plan.groupColumns) {
      const diff = columnValue(left, plan.table, column, plan.dateColumn)
        .localeCompare(columnValue(right, plan.table, column, plan.dateColumn));
      if (diff !== 0) return diff;
    }

    const leftYear = parseYear(plan.dateColumn ? left.values[plan.dateColumn] : undefined) ?? Number.MAX_SAFE_INTEGER;
    const rightYear = parseYear(plan.dateColumn ? right.values[plan.dateColumn] : undefined) ?? Number.MAX_SAFE_INTEGER;
    return leftYear - rightYear;
  });
}

function sortRowsByDate(plan: TablePlan): TableRow[] {
  const direction = plan.sortDirection || 'asc';
  const multiplier = direction === 'desc' ? -1 : 1;

  return [...plan.rows].sort((left, right) => {
    const leftDate = parseDateTime(plan.dateColumn ? left.values[plan.dateColumn] : undefined);
    const rightDate = parseDateTime(plan.dateColumn ? right.values[plan.dateColumn] : undefined);

    if (typeof leftDate === 'number' && typeof rightDate === 'number' && leftDate !== rightDate) {
      return (leftDate - rightDate) * multiplier;
    }
    if (typeof leftDate === 'number' && typeof rightDate !== 'number') return -1;
    if (typeof leftDate !== 'number' && typeof rightDate === 'number') return 1;

    const leftName = Object.values(left.values).join(' ');
    const rightName = Object.values(right.values).join(' ');
    return leftName.localeCompare(rightName);
  });
}

function buildRowOutputColumns(plan: TablePlan): PlannedColumn[] {
  const knownColumns = [
    matchColumn(plan.table.headers, 'property'),
    matchColumn(plan.table.headers, 'building'),
    matchColumn(plan.table.headers, 'unit'),
    matchColumn(plan.table.headers, 'equipment'),
    matchColumn(plan.table.headers, 'type'),
    matchColumn(plan.table.headers, 'manufacturer'),
    matchColumn(plan.table.headers, 'model'),
    matchColumn(plan.table.headers, 'serial'),
    plan.dateColumn,
    matchColumn(plan.table.headers, 'refrigerant'),
    matchColumn(plan.table.headers, 'tonnage'),
  ].filter((value): value is string => Boolean(value));

  const seen = new Set<string>();
  return knownColumns
    .filter((header) => {
      if (seen.has(header)) return false;
      seen.add(header);
      return true;
    })
    .map((header) => ({ label: header, source: header }));
}

function buildGroupedRowsAnswer(plan: TablePlan): string {
  const knownColumns = [
    matchColumn(plan.table.headers, 'type'),
    matchColumn(plan.table.headers, 'property'),
    matchColumn(plan.table.headers, 'unit'),
    matchColumn(plan.table.headers, 'equipment'),
    matchColumn(plan.table.headers, 'manufacturer'),
    matchColumn(plan.table.headers, 'model'),
    plan.dateColumn,
    matchColumn(plan.table.headers, 'tonnage'),
    matchColumn(plan.table.headers, 'refrigerant'),
  ].filter((value): value is string => Boolean(value));
  const groupSourceColumns = new Set(plan.groupColumns.map((column) => column.source).filter(Boolean));
  const outputColumns = [
    ...plan.groupColumns,
    { label: 'Installed Year', kind: 'installed_year' as const },
    ...knownColumns.filter((header) => !groupSourceColumns.has(header)).map((header) => ({ label: header, source: header })),
  ];
  const includeInferenceBasis = plan.groupColumns.some((column) => column.kind === 'refrigerant_group');
  const lines = [
    `Here is the exact row-level organization from the uploaded table.`,
    '',
    `- Total matching rows: ${formatNumber(plan.rows.length)}`,
    `- Sorted by: ${plan.groupColumns.map((column) => column.label).join(', ')}`,
    ...(includeInferenceBasis ? ['- Blank refrigerant values are inferred from install date and model/manufacturer where possible'] : []),
    '',
    '```csv',
    csvRow([...outputColumns.map((column) => column.label), ...(includeInferenceBasis ? ['Inference Basis'] : [])]),
  ];

  sortRowsForPlan(plan).forEach((row) => {
    const values = outputColumns.map((column) => columnValue(row, plan.table, column, plan.dateColumn));
    if (includeInferenceBasis) values.push(inferRefrigerant(row, plan.table, plan.dateColumn).basis);
    lines.push(csvRow(values));
  });

  lines.push('```');
  return lines.join('\n');
}

function buildSortedRowsAnswer(plan: TablePlan): string {
  const outputColumns = [
    { label: 'Installed Year', kind: 'installed_year' as const },
    ...buildRowOutputColumns(plan),
  ];
  const directionLabel = plan.sortDirection === 'desc' ? 'descending (newest first)' : 'ascending (oldest first)';
  const datedCount = plan.dateColumn ? plan.rows.filter((row) => parseDateTime(row.values[plan.dateColumn || ''])).length : 0;
  const lines = [
    `Here is the exact row-level list from the uploaded table, sorted by ${plan.dateColumn || 'date'} ${directionLabel}.`,
    '',
    `- Total matching rows: ${formatNumber(plan.rows.length)}`,
    ...(plan.dateColumn
      ? [
          `- Rows with ${plan.dateColumn}: ${formatNumber(datedCount)}`,
          `- Rows missing ${plan.dateColumn}: ${formatNumber(plan.rows.length - datedCount)} (listed last)`,
        ]
      : []),
    '',
    '```csv',
    csvRow(outputColumns.map((column) => column.label)),
  ];

  sortRowsByDate(plan).forEach((row) => {
    lines.push(csvRow(outputColumns.map((column) => columnValue(row, plan.table, column, plan.dateColumn))));
  });

  lines.push('```');
  return lines.join('\n');
}

function buildPivotCountAnswer(plan: TablePlan): string {
  const propertyColumn = plan.groupColumns[0]?.source;
  if (!propertyColumn || !plan.dateColumn) return buildGroupCountAnswer(plan);

  const datedRows = plan.rows.filter((row) => parseYear(row.values[plan.dateColumn || '']));
  const properties = Array.from(new Set(datedRows.map((row) => row.values[propertyColumn]).filter(Boolean)));
  const years = Array.from(new Set(datedRows.map((row) => parseYear(row.values[plan.dateColumn || ''])).filter((year): year is number => Boolean(year))))
    .sort((left, right) => left - right);
  const counts = new Map<string, number>();
  datedRows.forEach((row) => {
    const year = parseYear(row.values[plan.dateColumn || '']);
    const property = row.values[propertyColumn] || '(blank)';
    counts.set(`${year}__${property}`, (counts.get(`${year}__${property}`) || 0) + 1);
  });

  const lines = [
    `Here is the exact count by year and ${propertyColumn} from the uploaded table.`,
    '',
    `- Total matching rows: ${formatNumber(plan.rows.length)}`,
    `- Rows included in the year grid: ${formatNumber(datedRows.length)}`,
    `- Rows missing ${plan.dateColumn} and excluded from the year grid: ${formatNumber(plan.rows.length - datedRows.length)}`,
    '',
    '```csv',
    csvRow(['Year', ...properties, 'Total']),
  ];

  years.forEach((year) => {
    const rowCounts = properties.map((property) => counts.get(`${year}__${property}`) || 0);
    lines.push(csvRow([String(year), ...rowCounts.map(String), String(rowCounts.reduce((sum, value) => sum + value, 0))]));
  });

  const totals = properties.map((property) => years.reduce((sum, year) => sum + (counts.get(`${year}__${property}`) || 0), 0));
  lines.push(csvRow(['Total', ...totals.map(String), String(datedRows.length)]));
  lines.push('```');
  return lines.join('\n');
}

function answerFromPlan(plan: TablePlan): string {
  if (plan.operation === 'coverage') return buildCoverageAnswer(plan);
  if (plan.operation === 'pivot_count') return buildPivotCountAnswer(plan);
  if (plan.operation === 'sorted_rows') return buildSortedRowsAnswer(plan);
  if (plan.operation === 'grouped_rows') return buildGroupedRowsAnswer(plan);
  return buildGroupCountAnswer(plan);
}

export function buildStructuredSpreadsheetResponse(
  query: string,
  documentContents: DocumentContextChunk[] | undefined,
  activeContext?: ActiveContext,
  conversationHistory?: Array<{ role: string; content: string }>
): SpreadsheetStructuredAnswer | null {
  if (!documentContents || documentContents.length === 0) return null;
  const dataset = getSpreadsheetDataset(documentContents);
  if (!dataset) return null;
  const plan = buildPlan(query, dataset, activeContext, conversationHistory);
  if (!plan) return null;
  return {
    answer: answerFromPlan(plan),
    groundingSource: STRUCTURED_GROUNDING_SOURCE,
    citations: [],
  };
}
